// Profile tab: homepage, LinkedIn and affiliation/country edits. Opening a field doesn't save;
// the dialog's OK button (or Enter/Tab) commits. Shared by ProfileMeta (the fields),
// Input (the bio card, inactive while a field is open) and the dialog footer (the button).
// Drafts live in local storage as the user types: confirming saves and clears them; discarding
// (Escape, click outside) clears them; closing the dialog keeps them for the next opening.

import {computed, reactive, ref} from "vue";
import {useEventListener, useLocalStorage} from "@vueuse/core";
import {useSaveUrl} from "@/composables/useSaveUrl";
import {useSaveLinkedIn} from "@/composables/useSaveLinkedIn";
import {useSaveAffiliation} from "@/composables/useSaveAffiliation";

export const LINKEDIN_PREFIX = "linkedin.com/in/";
export const HOMEPAGE_PREFIX = "https://";
export const AFFILIATION_MAX_LENGTH = 50; // the affiliation and the country, each

const openFields = reactive({url: false, linkedIn: false, affiliation: false});
const drafts = reactive({
  url: useLocalStorage("profile-draft-url", ""),
  linkedInSlug: useLocalStorage("profile-draft-linkedIn", ""),
  affiliation: useLocalStorage("profile-draft-affiliation", ""),
  country: useLocalStorage("profile-draft-country", "")
});
const DRAFTS_OF_FIELD = {
  url: ["url"], linkedIn: ["linkedInSlug"], affiliation: ["affiliation", "country"]
};
const isCommitting = ref(false);
const error = ref("");

// The homepage is edited after a fixed "https://": "http://example.org/x" → "example.org/x"
export function getHomepageRest(url) {
  return String(url || "").trim().replace(/^https?:\/\//i, "");
}

function getHomepageUrl(rest) {
  const cleanRest = getHomepageRest(rest);
  return cleanRest ? `${HOMEPAGE_PREFIX}${cleanRest}` : ""; // empty removes the homepage
}

// "https://www.linkedin.com/in/drseanodonoghue/" → "drseanodonoghue"
export function getLinkedInSlug(url) {
  return String(url || "").match(/linkedin\.com\/in\/([^/?#\s]+)/i)?.[1] || "";
}

// what's typed or pasted into the LinkedIn field: a LinkedIn address (any case, ending /in/name,
// a trailing "/" and anything after "?" or "#" ignored) becomes just its name; anything else is
// kept as typed. e.g. "https://www.LinkedIn.com/in/jane-doe/?originalSubdomain=uk" → "jane-doe"
export function getLinkedInInput(value) {
  const text = String(value || "");
  const address = text.trim().split(/[?#]/)[0];
  return address.match(/linkedin.*\/in\/([^/\s]+)\/?$/i)?.[1] || text;
}

// Shown in red; never saved: committing closes the field and keeps the previous value
export function getIsLinkedInSlugInvalid(slug) {
  const trimmed = String(slug || "").trim();
  return !trimmed || trimmed.includes("/");
}

function getOpenNames() {
  return Object.keys(openFields).filter((name) => openFields[name]);
}

function closeField(name) {
  openFields[name] = false;
  error.value = "";
}

function clearDrafts(name) {
  for (const key of DRAFTS_OF_FIELD[name]) drafts[key] = "";
}

// Escape or click outside: the edit is thrown away, and so is its draft
function discardField(name) {
  clearDrafts(name);
  closeField(name);
}

function discardAll() {
  for (const name of getOpenNames()) discardField(name);
}

// dialog closed or tab changed: fields close, drafts stay for next time
function closeAll() {
  for (const name of Object.keys(openFields)) openFields[name] = false;
  error.value = "";
}

async function saveField(name, user, savers) {
  if (name === "url") return savers.saveUrl(user, getHomepageUrl(drafts.url));
  if (name === "affiliation") {
    return savers.saveAffiliation(user, drafts.affiliation.trim(), drafts.country.trim());
  }
  return savers.saveLinkedIn(user, `https://www.${LINKEDIN_PREFIX}${drafts.linkedInSlug.trim()}`);
}

// Escape or a click in the dialog outside the open edit (.open-field) discards it, before the
// dialog's own Escape handler (capture phase). OK ([data-profile-commit]) and Cancel
// ([data-profile-cancel]) are exempt: they act themselves. Discarding on Cancel's pointerdown
// swapped the footer to Sign Out / Close mid-click, so the click landed on Sign Out (2026-10-13).
// Used by ProfileMeta (homepage, LinkedIn, affiliation) and Name.vue
export function useDiscardOnOutside(getIsOpen, discard) {
  function onEscape(event) {
    if (event.key !== "Escape" || !getIsOpen()) return;
    event.preventDefault();
    event.stopPropagation();
    discard();
  }
  function onPointerDown(event) {
    if (!getIsOpen() || !event.target?.closest?.("dialog")) return;
    if (event.target.closest(".open-field, [data-profile-commit], [data-profile-cancel]")) return;
    discard();
  }
  useEventListener(window, "keydown", onEscape, {capture: true});
  useEventListener(window, "pointerdown", onPointerDown, {capture: true});
}

export function useProfileFieldEdits() {
  const {save: saveUrl} = useSaveUrl();
  const {save: saveLinkedIn} = useSaveLinkedIn();
  const {save: saveAffiliation} = useSaveAffiliation();
  const savers = {saveUrl, saveLinkedIn, saveAffiliation};
  const openNames = computed(getOpenNames);
  const isAnyOpen = computed(() => openNames.value.length > 0);
  // saves one open field, clears its draft and closes it; an invalid LinkedIn name just closes
  // (not saved; its draft stays, shown in red next time)
  async function commitField(name, user) {
    if (!openFields[name]) return true;
    if (name === "linkedIn" && getIsLinkedInSlugInvalid(drafts.linkedInSlug)) {
      closeField(name);
      return true;
    }
    const isSaved = await saveField(name, user, savers);
    if (!isSaved) error.value = "Couldn't save. Please try again.";
    if (!isSaved) return false;
    clearDrafts(name);
    openFields[name] = false;
    return true;
  }
  // the bottom button and Enter: every open field, one at a time (each save reloads the user)
  async function commit(user) {
    if (isCommitting.value) return false;
    error.value = "";
    isCommitting.value = true;
    for (const name of [...openNames.value]) {
      if (!(await commitField(name, user))) break;
    }
    isCommitting.value = false;
    return !error.value;
  }
  return {
    openFields, drafts, error, isCommitting, isAnyOpen,
    commit, commitField, discardField, discardAll, closeAll
  };
}
